/**
 * Prepara a foto do comprovante para envio.
 *
 * O iPhone fotografa em HEIC desde que "Alta eficiencia" e o padrao, e o
 * formato era recusado no envio: o motorista tirava a foto do cupom e recebia
 * "Envie uma foto (JPG ou PNG) ou um PDF", sem entender por que. Converter aqui
 * resolve na origem e ainda arruma dois problemas de lado: HEIC nao abre no
 * Chrome nem no Android, entao o comprovante ficaria ilegivel para quem confere
 * depois; e foto de celular passa dos 4 MB, o que pesa no 4G da estrada e no
 * disco do servidor.
 *
 * Nada disso e obrigatorio para o envio funcionar: se o navegador nao souber
 * decodificar a imagem, devolvemos o arquivo original e o servidor decide.
 */

/** Maior lado da imagem convertida. Cupom fiscal continua legivel nesta escala. */
const LADO_MAXIMO = 2000;
const QUALIDADE = 0.85;

/**
 * Acima disto a foto e reduzida mesmo ja sendo JPEG. O limite do sistema e 10 MB
 * por arquivo, mas foto grande demora no 4G do posto e enche o disco do
 * servidor sem melhorar em nada a leitura de um cupom.
 */
const TAMANHO_SEM_CONVERSAO = 2 * 1024 * 1024;

const ehImagem = (arquivo: File): boolean => arquivo.type.startsWith('image/');

/** JPEG ou PNG pequeno segue como esta: nao ha o que ganhar convertendo. */
const dispensaConversao = (arquivo: File): boolean =>
  (arquivo.type === 'image/jpeg' || arquivo.type === 'image/png')
  && arquivo.size <= TAMANHO_SEM_CONVERSAO;

const carregar = (arquivo: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const endereco = URL.createObjectURL(arquivo);
    const imagem = new Image();

    imagem.onload = () => {
      URL.revokeObjectURL(endereco);
      resolve(imagem);
    };
    imagem.onerror = () => {
      URL.revokeObjectURL(endereco);
      reject(new Error('Navegador não decodificou a imagem.'));
    };
    imagem.src = endereco;
  });

const paraJpeg = (imagem: HTMLImageElement, nome: string): Promise<File> =>
  new Promise((resolve, reject) => {
    const escala = Math.min(1, LADO_MAXIMO / Math.max(imagem.width, imagem.height));
    const tela = document.createElement('canvas');
    tela.width = Math.round(imagem.width * escala);
    tela.height = Math.round(imagem.height * escala);

    const contexto = tela.getContext('2d');
    if (!contexto) {
      reject(new Error('Canvas indisponível.'));
      return;
    }

    contexto.drawImage(imagem, 0, 0, tela.width, tela.height);
    tela.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Não foi possível gerar o JPEG.'));
          return;
        }
        resolve(new File([blob], nome, { type: 'image/jpeg', lastModified: Date.now() }));
      },
      'image/jpeg',
      QUALIDADE,
    );
  });

/** Troca a extensao do arquivo original por .jpg. */
const nomeJpeg = (nome: string): string => `${nome.replace(/\.[^.]+$/, '') || 'foto'}.jpg`;

export const prepararImagemParaEnvio = async (arquivo: File): Promise<File> => {
  if (!ehImagem(arquivo) || dispensaConversao(arquivo)) {
    return arquivo;
  }

  try {
    const imagem = await carregar(arquivo);
    return await paraJpeg(imagem, nomeJpeg(arquivo.name));
  } catch {
    // Sem conversao o envio segue com o arquivo como veio: pior recusar aqui do
    // que deixar o servidor responder o que sabe sobre o formato.
    return arquivo;
  }
};
